/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { ReactNode } from 'react';
import { css, StyleSheet } from 'aphrodite';

import { BrandColors } from '@blend/enterprise-components';

import { isClosingRON, findOptionByValue, notarizationPlatformOptions } from './ScheduleClosingTaskUtils';
import { ScheduleClosingAppointmentTask } from '../ScheduleClosingAppointmentTask';
import { BlendBridge } from 'blb-types/blendBridge';

const styles = StyleSheet.create({
  confirmationBody: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  confirmationHeader: {
    color: BrandColors.NIGHT_90,
    fontSize: '14px',
    lineHeight: '20px',
  },
  confirmationSection: {
    marginTop: '24px',
  },
  confirmationSectionHeader: {
    color: BrandColors.NIGHT_90,
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '20px',
    marginBottom: '8px',
  },
  confirmationSectionRow: {
    color: BrandColors.NIGHT_90,
    display: 'flex',
    flexDirection: 'row',
    fontSize: '12px',
    lineHeight: '14px',
    marginBottom: '16px',
  },
  confirmationSectionRowLabel: {
    width: '40%',
  },
  confirmationSectionRowValue: {
    width: '60%',
  },
});

/**
 * Renders a Confirmation UI for ScheduleClosingAppointmentTask.
 * @param  {ScheduleClosingAppointmentTask.props}            props    Props of Parent
 * @param  {BlendBridge.API.ScheduleClosingTaskCompleteMeta} formData Form metadata about to be sent to EClose
 * @return {ReactNode}                                                Confirmation UI
 * @function
 */
export const renderConfirmationScreen = (
  props: ScheduleClosingAppointmentTask.Props,
  formData: BlendBridge.API.ScheduleClosingTaskCompleteMeta,
): ReactNode => {
  const { appointmentInformation, notaryInformation } = formData;
  const isRON = isClosingRON(props.closingType);
  const notarizationLabel: string = isRON ? 'Remote online notarization platform' : 'Closing method';
  const timeValue = findOptionByValue(props.timeOptions ?? [], appointmentInformation.time)?.text;
  const notarizationOptions = isRON ? notarizationPlatformOptions(props.closingType) : props.notarizationMethodOptions;
  const notarizationValue = findOptionByValue(notarizationOptions ?? [], appointmentInformation.notarization)?.text;

  return (
    <>
      <div className={css(styles.confirmationHeader)}>
        Once you schedule this closing appointment, an email confirmation will be sent to the borrower and you will no
        longer be able to edit the appointment or notary information.
      </div>
      <div className={css(styles.confirmationBody)}>
        <div className={css(styles.confirmationSection)}>
          <h1 className={css(styles.confirmationSectionHeader)}>Appointment information</h1>
          <dl>
            <div className={css(styles.confirmationSectionRow)}>
              <dd className={css(styles.confirmationSectionRowLabel)}>Date</dd>
              <dt className={css(styles.confirmationSectionRowValue)}>{appointmentInformation.date}</dt>
            </div>
            <div className={css(styles.confirmationSectionRow)}>
              <dd className={css(styles.confirmationSectionRowLabel)}>Time</dd>
              <dt className={css(styles.confirmationSectionRowValue)}>{timeValue}</dt>
            </div>
            <div className={css(styles.confirmationSectionRow)}>
              <dd className={css(styles.confirmationSectionRowLabel)}>{notarizationLabel}</dd>
              <dt className={css(styles.confirmationSectionRowValue)}>{notarizationValue}</dt>
            </div>
            {!isRON && appointmentInformation.address && (
              <div className={css(styles.confirmationSectionRow)}>
                <dd className={css(styles.confirmationSectionRowLabel)}>Address</dd>
                <dt className={css(styles.confirmationSectionRowValue)}>
                  {appointmentInformation.address.addressLineOne}
                  <br />
                  {appointmentInformation.address.addressLineTwo && (
                    <>
                      {appointmentInformation.address.addressLineTwo}
                      <br />
                    </>
                  )}
                  {appointmentInformation.address.city}, {appointmentInformation.address.state}{' '}
                  {appointmentInformation.address.zipCode}
                </dt>
              </div>
            )}
          </dl>
        </div>
        <div className={css(styles.confirmationSection)}>
          <h1 className={css(styles.confirmationSectionHeader)}>Notary information</h1>
          <dl>
            <div className={css(styles.confirmationSectionRow)}>
              <dd className={css(styles.confirmationSectionRowLabel)}>Name</dd>
              <dt className={css(styles.confirmationSectionRowValue)}>
                {notaryInformation.firstName} {notaryInformation.lastName}
              </dt>
            </div>
            <div className={css(styles.confirmationSectionRow)}>
              <dd className={css(styles.confirmationSectionRowLabel)}>Email</dd>
              <dt className={css(styles.confirmationSectionRowValue)}>{notaryInformation.email}</dt>
            </div>
            <div className={css(styles.confirmationSectionRow)}>
              <dd className={css(styles.confirmationSectionRowLabel)}>Phone call</dd>
              <dt className={css(styles.confirmationSectionRowValue)}>{notaryInformation.phoneNumber}</dt>
            </div>
          </dl>
        </div>
      </div>
    </>
  );
};
